/* eslint-disable @typescript-eslint/no-explicit-any */
import { ArrowLeft } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Domain, DomainUpdate } from '../../types';
import { domainService } from '../../services/domainService';
import { DomainForm } from '../../components/Domains/DomainForm';
import { Button } from '../../components/Common/button';
import { extractApiErrorMessage } from '../../lib/apiError';


const EditDomainPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { domainId } = useParams<{ domainId: string }>();
  const [domain, setDomain] = useState<Domain | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDomain = async () => {
      if (!domainId) {
        setError(t('domains.edit.missingId'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const domainData = await domainService.getDomainDetails(domainId);
        setDomain(domainData);
      } catch (err: any) {
        console.error('Failed to load domain', err);
        setError(
          extractApiErrorMessage(err, t('domains.edit.loadError'))
        );
      } finally {
        setLoading(false);
      }
    };

    loadDomain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  const handleSubmit = async (data: DomainUpdate) => {
    if (!domainId) return;

    try {
      setSubmitting(true);
      setError(null);

      // Update the domain
      await domainService.updateDomain(domainId, data);

      // Navigate back to domain detail page
      navigate(`/domains/${domainId}`, {
        state: { message: t('domains.edit.updateSuccess') }
      });
    } catch (err: any) {
      console.error('Failed to update domain', err);
      setError(
        extractApiErrorMessage(err, t('domainDetail.updateError'))
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/domains/${domainId}`);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="h-8 w-20 bg-muted rounded animate-pulse"></div>
          <div>
            <div className="h-8 w-48 bg-muted rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-muted rounded animate-pulse"></div>
          </div>
        </div>

        <div className="max-w-2xl">
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="space-y-6">
              <div className="h-16 bg-muted rounded animate-pulse"></div>
              <div className="h-24 bg-muted rounded animate-pulse"></div>
              <div className="h-12 bg-muted rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !domain) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/domains')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('global.back')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('global.error')}</h1>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/domains')} variant="outline">
            {t('domains.edit.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('global.back')}
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('domains.edit.title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('domains.edit.subtitle')}
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Form */}
      <div className="max-w-2xl">
        <div className="bg-card rounded-lg border border-border p-6">
          <DomainForm
            domain={domain || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={submitting}
          />
        </div>
      </div>
    </div>
  );
};

export default EditDomainPage;
